import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Typography, IconButton } from '@mui/material';
import { blue, brown, green, orange, red, yellow } from '@mui/material/colors';
import { ContactSupportOutlined, Clear as ClearIcon } from '@mui/icons-material';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { TypographyStyled } from '../../../components/general/mini_components';
import { ColourChip } from '../../../components/general/mini_components-ts';
import Loader from '../../../components/Loader';
import { HeadCell, IPatient, IResearcher, ISurvey } from '../../../constants/types';
import { useDepartments, useCurrentInvestigation } from '../../../hooks';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { dateAndTimeFromPostgresString, decryptSinglePatientData, fullDateFromPostgresString, getDepartmentFromUnit, getHealthIDLabel, getPatientID, patientFullName, researcherFullName, stringDatePostgresToDate, formatDateByLocale } from '../../../utils/index.jsx';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import axios from '../../../utils/axios';
import { useRequests } from '../../../hooks/useRequests';
import { useSurgeryAppointments } from '../../../hooks/useSurgeryAppointments';

import { IRequest, RequestStatus, RequestType, ServiceType } from './types';
import SearchBox from '../../../components/general/SearchBox';
import { calculateTotalDays, getRequestStatusName, labStatusValues, surgeryStatusValues } from './utils';
import { fetchPatient } from '../../../db';
import { getSubmissionPatientService } from '../../../services';
import Modal from '../../../components/general/modal';
import { DocumentPDF } from '../Document';
import { RequestPDFDocument } from '../../../components/pdf/documents/RequestPDFDocument';
import { PDFDownloadButton } from '../../../components/pdf/components/PDFDownloadButton';
import { createT } from '../../../components/pdf/utils/simpleTranslate';

const requestGroupStatus = (request:IRequest) => {
    // Si una request es completada, el estado es completada. Si no, devuelvo los estados de las requests
    if(request.requestsServiceInvestigation.some(request => request.status === RequestStatus.COMPLETED)){
        return <RequestStatusToChip status={RequestStatus.COMPLETED} />
    }
    else{
        return request.requestsServiceInvestigation.map((req) => {
            return <RequestStatusToChip status={req.status} />
        }) 
    }
    
}

export const statusToColor = (status:RequestStatus) => {
    let colour = "#000";
    switch(status){
        case RequestStatus.PENDING_APPROVAL:
        case RequestStatus.PENDING_PAYMENT:
        case RequestStatus.IN_PROGRESS:
        case RequestStatus.PENDING_ANESTHESIOLOGIST:
        case RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS:
            colour = yellow[900];
            break;
        case RequestStatus.COMPLETED:     
        case RequestStatus.ACCEPTED: 
            colour = blue[900];
            break;
        case RequestStatus.SOME_ACCEPTED:
        case RequestStatus.INCOMPLETE_ACCEPTED:
            colour = blue[600];
            break;
    
        case RequestStatus.ANESTHESIOLOGIST_ACCEPTED:
        case RequestStatus.PAYMENT_COMPLETED:
        case RequestStatus.READY_FOR_SURGERY:
            colour = green[700];            
            break;
        case RequestStatus.CANCELED:
        case RequestStatus.DENIED:
        case RequestStatus.INCOMPLETE:
        case RequestStatus.ANESTHESIOLOGIST_REJECTED:
            colour = red[500];            
            break;  
    }
    return colour;
}

export const RequestStatusToChip:React.FC<{status:RequestStatus}> = ({status}) =>  {
        let colour = statusToColor(status);
        let translation = RequestStatus[status]
       
        return <ColourChip rgbcolor={colour} label={<Translate id={`pages.hospital.services.request_status.${translation}`}  />}/>
}

export const serviceToColor = (type:ServiceType) => {
    let colour = null
    switch(type){
        case ServiceType.LABORATORY:
            colour = orange[900];
            break;
        case ServiceType.IMAGING:
            colour = yellow[900];
            break;
        case ServiceType.PHARMACY:
            colour = green[900];
            break;
        case ServiceType.SHOE:
            colour = brown[900];
            break;
    }
    return colour;
}

const ServiceTypeToChip:React.FC<{type:ServiceType}> = ({type}) =>  {
    let colour = serviceToColor(type);
    let translation = ServiceType[type];
    return <ColourChip rgbcolor={colour} label={<Translate id={`pages.hospital.services.service_type.${translation}`}/>} />
}


interface RequestTableProps {
    requestServiceType:ServiceType,
    activeLanguageCode:string,
    typeRequest:RequestType,
    uuidPatient?:string,
    noSearchBox?:boolean,
    uuidInvestigation:string,
    showActions?:boolean,
    surveys?:ISurvey[],
    searchFor:string,
    fillPending?:boolean,
    patient?:IPatient, // Add optional patient prop
    encryptionData?:{
        encryptedKeyUsed:number,
        keyResearcherInvestigation:string,
        permissions:any[],
        personalFields:any[],
    },
    callBackRequestSelected:(request:IRequest)=>void,
    createAppointmentFromRequestCallback:(requestId:number) => void,
    goToAppointmentFromRequestCallback:(uuidAppointment:string) => void,
    goToBillingPatientCallback:(uuidPatient:string) => void
}

type RequestTablePharmacyProps = Pick<RequestTableProps, 'requestServiceType' | 'uuidInvestigation' | 'callBackRequestSelected'>

const ACTIONABLE_REQUESTS = [RequestStatus.PENDING_APPROVAL, RequestStatus.ACCEPTED, RequestStatus.COMPLETED, RequestStatus.IN_PROGRESS, RequestStatus.PENDING_ANESTHESIOLOGIST, RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS];

export const RequestTableService:React.FC<RequestTableProps> = ({requestServiceType, uuidPatient, uuidInvestigation, showActions, activeLanguageCode,
                                                                    surveys, fillPending, encryptionData, patient, callBackRequestSelected, 
                                                                    createAppointmentFromRequestCallback, goToAppointmentFromRequestCallback, goToBillingPatientCallback}) => {
    
    return <RequestTable requestServiceType={requestServiceType} uuidPatient={uuidPatient} searchFor="general.patient"
                uuidInvestigation={uuidInvestigation} showActions={showActions} surveys={surveys} typeRequest={RequestType.SERVICES}
                fillPending={fillPending} encryptionData={encryptionData} activeLanguageCode={activeLanguageCode}
                patient={patient}
                createAppointmentFromRequestCallback={createAppointmentFromRequestCallback}
                goToAppointmentFromRequestCallback={goToAppointmentFromRequestCallback}
                goToBillingPatientCallback={goToBillingPatientCallback}
                callBackRequestSelected={callBackRequestSelected} />
}

export const RequestTablePharmacy:React.FC<RequestTablePharmacyProps> = ({requestServiceType, uuidInvestigation, callBackRequestSelected, activeLanguageCode}) => {
    return <RequestTable requestServiceType={requestServiceType} searchFor="hospital.search_box.staff" typeRequest={RequestType.PHARMACY}
                uuidInvestigation={uuidInvestigation} activeLanguageCode={activeLanguageCode}
                callBackRequestSelected={callBackRequestSelected} />
}

const RequestTable: React.FC<RequestTableProps> = ({
    typeRequest, 
    requestServiceType,
    searchFor, 
    noSearchBox, 
    surveys, 
    activeLanguageCode,
    uuidPatient, 
    uuidInvestigation, 
    encryptionData, 
    showActions,
    fillPending,
    patient, // Add patient prop
    callBackRequestSelected, 
    createAppointmentFromRequestCallback,
    goToAppointmentFromRequestCallback,
    goToBillingPatientCallback
}) => {
    const { 
        requests, 
        loading: requestsLoading, 
        error: requestsError 
    } = useRequests({
        requestServiceType,
        uuidPatient,
        uuidInvestigation,
        typeRequest
    });

    const { 
        appointmentsByRequest, 
        loading: appointmentsLoading, 
        error: appointmentsError 
    } = useSurgeryAppointments({
        uuidInvestigation,
        enabled: requestServiceType === ServiceType.SURGERY
    });

    const enrichedRequests = useMemo(() => {
        if (!requests) return [];

        if(appointmentsByRequest.length === 0){
            return requests;
        }
        
        return requests.map(request => {
            const appointmentWithRequest = appointmentsByRequest[request.id];
            return {
                ...request,
                appointment: appointmentWithRequest ? appointmentWithRequest : null
            }
        });
    }, [requests, appointmentsByRequest]);


    if (requestsLoading || appointmentsLoading || !requests) {
        return <Loader />;
    }

    if (requestsError || appointmentsError) {
        console.error('Errors:', { requestsError, appointmentsError });
        // You might want to show an error message to the user
    }

    if (requests.length > 0) {
        return (
            <RequestTableComponent 
                searchFor={searchFor} 
                noSearchBox={noSearchBox} 
                requestServiceType={requestServiceType} 
                surveys={surveys} 
                fillPending={fillPending}  
                encryptionData={encryptionData} 
                uuidPatient={uuidPatient}
                uuidInvestigation={uuidInvestigation}
                showActions={showActions}
                activeLanguageCode={activeLanguageCode}
                patient={patient}
                loading={requestsLoading || appointmentsLoading} 
                requests={enrichedRequests} 
                callBackRequestSelected={callBackRequestSelected}
                createAppointmentFromRequestCallback={createAppointmentFromRequestCallback}
                goToAppointmentFromRequestCallback={goToAppointmentFromRequestCallback}
                goToBillingPatientCallback={goToBillingPatientCallback}
            />
        );
    }

    return (
        <TypographyStyled variant="body2">
            <Translate id="pages.hospital.services.no_requests" />
        </TypographyStyled>
    );
};

export default RequestTable;

interface RequestTableComponentProps extends RequestTableProps {
    requests:IRequest[],
    loading:boolean,
    createAppointmentFromRequestCallback:(requestId:number) => void,
    goToAppointmentFromRequestCallback:(uuidAppointment:string) => void,
    goToBillingPatientCallback:(uuidPatient:string) => void
}

interface Row {
    id: number;
    nhc: string | number;
    service: string | JSX.Element;
    serviceName: string; // Add missing property
    department: string;
    patient: string;
    researcher: string;
    submissionPatient: any;
    status: JSX.Element;
    statusValue: RequestStatus;
    type: string | JSX.Element;
    date: string;
    scheduled: boolean; // Add missing property
    uuidResearcher: string; // Add missing property
    dateCompletedRaw: Date | null; // Raw completion date for filtering
}
export const RequestTableComponent: React.FC<RequestTableComponentProps> = ({ uuidPatient, uuidInvestigation, searchFor, requests, requestServiceType, noSearchBox, surveys,
                                                                                encryptionData, loading, patient, callBackRequestSelected, createAppointmentFromRequestCallback, 
                                                                                goToAppointmentFromRequestCallback, goToBillingPatientCallback, activeLanguageCode }) => {
    
    // Normalize locale: ensure it's 'en', 'es', or 'fr'
    const normalizedLocale = activeLanguageCode?.toLowerCase().startsWith('en') ? 'en' : 
                             activeLanguageCode?.toLowerCase().startsWith('es') ? 'es' : 'fr';
    const t = createT(normalizedLocale);
    
    const {departments} = useDepartments();
    const { isBillingEnabled } = useCurrentInvestigation();
    const pdfConfig = usePDFConfig(); // Add PDF configuration hook
    
    // Get investigation UUID for signature fetching
    const currentInvestigationUuid = useSelector((state: any) => 
        state.investigations.currentInvestigation?.uuid
    );    
    const [rows, setRows] = React.useState<Row[]>([]);
    const [filteredRows, setFilteredRows] = React.useState<Row[]>([]);
    const [searchText, setSearchText] = React.useState("");
    const [statusFilter, setStatusFilter] = React.useState<RequestStatus[]>([]);
    const [scheduledFilter, setScheduledFilter] = React.useState<boolean>(false);
    const [stringHealthID, setStringHealthID] = React.useState(getHealthIDLabel());

    const [proceduresApplied, setProceduresApplied] = React.useState<string[]>([]);
    const [procedureFilter, setProcedureFilter] = React.useState<string>("");
    const [surgeonsFilter, setSurgeonsFilter] = React.useState<string>("");
    const [surgeonsScheduled, setSurgeonsScheduled] = React.useState<IResearcher[]>([]);
    const [startDateFilter, setStartDateFilter] = React.useState<Dayjs | null>(null);
    const [endDateFilter, setEndDateFilter] = React.useState<Dayjs | null>(null);
    const [showModalPDF, setShowModalPDF] = React.useState<boolean>(false);
    const [requestPDF, setRequestPDF] = React.useState<IRequest | null>(null);
    const [patientForPDF, setPatientForPDF] = React.useState<IPatient | null>(null);
    const [fullSubmission, setFullSubmission] = React.useState<any>(null);
    const [loadingPDFData, setLoadingPDFData] = React.useState<boolean>(false);

    const filterItems = useMemo(() => {
        const requestStatusValues = requestServiceType === ServiceType.SURGERY ? surgeryStatusValues : labStatusValues;
        
        let filterItems = requestStatusValues.map((status) => ({label:`pages.hospital.services.request_status.${getRequestStatusName(status)}`, selected: statusFilter.includes(status), value:status, color:statusToColor(status), callBack:() => applyStatusFilter(status)}))
        if(requestServiceType === ServiceType.SURGERY){
            filterItems.push({label:`pages.hospital.services.request_status.scheduled`, selected: scheduledFilter, value:scheduledFilter, color:statusToColor(RequestStatus.READY_FOR_SURGERY), callBack:() => setScheduledFilter(!scheduledFilter)})
        }
        return filterItems;
    }, [statusFilter, scheduledFilter]);

    useEffect(() => {
        const uniqueProcedures = requests.reduce((acc: string[], request) => {
            const procedureName = request.requestsServiceInvestigation.length > 0 ? request.requestsServiceInvestigation[0].serviceInvestigation.service.name : "";
            if (!acc.includes(procedureName)) {
                acc.push(procedureName);
            }
            return acc;
        }, []);
        setProceduresApplied(uniqueProcedures);
    }, [requests]);

    useEffect(() => {
        const uniqueStaffs = requests.reduce((acc: IResearcher[], request) => {
            const mainSurgeon = request.appointment ? request.appointment.mainSurgeon?.researcher : null;
            if(mainSurgeon){
                const exists = acc.some(staff => staff.uuid === mainSurgeon.uuid);
                if (!exists) {
                acc.push(mainSurgeon);
            }}
            return acc;
        }, []);
        setSurgeonsScheduled(uniqueStaffs);
    }, [requests]);

    useEffect(() => {
        const loadHealthID = async () => {
            if (requests.length > 1) {
                const patient = requests[0].requestsServiceInvestigation.length > 0 ? requests[0].requestsServiceInvestigation[0].patientInvestigation : null;
                const patientData = patient ? await fetchPatient(patient.uuid) : null;
                const label = getHealthIDLabel(patientData);
                setStringHealthID(label);
            }
        };
        loadHealthID();
    }, [activeLanguageCode, requests]);

    useEffect(() => {
        setFilteredRows(rows.filter((row: Row) => {
            const searchItem = requestServiceType === ServiceType.PHARMACY ? row.researcher : row.patient;
            const healthIdFiltered = row.nhc.toString().toLocaleLowerCase().includes(searchText.toLocaleLowerCase());
            const statusFiltered = statusFilter.length === 0 ? true : statusFilter.includes(row.statusValue);
            const scheduledFiltered = scheduledFilter ? row.scheduled : true;
            const procedureFiltered = procedureFilter !== "" ? row.serviceName.toString().toLocaleLowerCase().includes(procedureFilter.toLocaleLowerCase()) : true;
            const staffFiltered = surgeonsFilter !== "" ? row.uuidResearcher === surgeonsFilter : true;
            
            // Date range filtering for completion date
            let dateFiltered = true;
            if (requestServiceType === ServiceType.SURGERY && row.dateCompletedRaw) {
                const completedDate = stringDatePostgresToDate(row.dateCompletedRaw);
                if (startDateFilter) {
                    const startDate = startDateFilter.startOf('day').toDate();
                    if (completedDate < startDate) {
                        dateFiltered = false;
                    }
                }
                if (endDateFilter && dateFiltered) {
                    const endDate = endDateFilter.endOf('day').toDate();
                    if (completedDate > endDate) {
                        dateFiltered = false;
                    }
                }
            } else if (requestServiceType === ServiceType.SURGERY && (startDateFilter || endDateFilter)) {
                // If date filters are set but row has no completion date, exclude it
                dateFiltered = false;
            }
            
            const filtered = (scheduledFilter || healthIdFiltered || searchItem.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) && statusFiltered && scheduledFiltered && procedureFiltered && staffFiltered && dateFiltered;
            return filtered;
                        
        }));
        
    }, [searchText, statusFilter, scheduledFilter, procedureFilter, surgeonsFilter, startDateFilter, endDateFilter, rows, requestServiceType]);
    
    useEffect(() => {
        const loadRows = async () => {
            const rowPromises = requests.sort((reqA, reqB) => 
                stringDatePostgresToDate(reqB.updatedAt).getTime() - stringDatePostgresToDate(reqA.updatedAt).getTime()
            ).map(async (request) => {            
                const uuidPatient = request.requestsServiceInvestigation[0] ? request.requestsServiceInvestigation[0].patientInvestigation.uuid : "";
                const patient = await fetchPatient(uuidPatient);
                console.log("uuidPatient", uuidPatient);
                console.log("requestId", request.id);
                
                const mainSurgeon = request.appointment?.mainSurgeon ? request.appointment.mainSurgeon : null;
                const mainSurgeonName = mainSurgeon ? researcherFullName(mainSurgeon.researcher) : "";
                const uuidResearcher = mainSurgeon ? mainSurgeon?.researcher.uuid : "";
                return {
                    id: request.id,
                    nhc: patient ? getPatientID(patient) : "",
                    service: request.requestsServiceInvestigation[0].serviceInvestigation.service.name ? request.requestsServiceInvestigation.map((req) => <ColourChip rgbcolor={serviceToColor(request.type)} label={req.serviceInvestigation.description} /> ) : "",
                    serviceName: request.requestsServiceInvestigation[0] ? request.requestsServiceInvestigation[0].serviceInvestigation.service.name : "",
                    department:request.departmentRequest ? request.departmentRequest.name : "",
                    patient: patient ? patientFullName(patient?.personalData) : "",
                    dob : patient?.personalData.birthdate ? new Date(patient?.personalData.birthdate).toLocaleDateString(activeLanguageCode) : "",
                    mainSurgeon: mainSurgeonName,
                    requester: researcherFullName(request.researcher),
                    uuidResearcher : uuidResearcher,        
                    status: <RequestStatusToChip status={request.status} />,
                    dateCompleted: request.completedAt ? fullDateFromPostgresString(activeLanguageCode, request.completedAt) : "",
                    dateCompletedRaw: request.completedAt ? request.completedAt : null,
                    submissionPatient: request.submissionPatient,
                    statusValue: request.status,
                    type : request.requestsServiceInvestigation[0] ? <ServiceTypeToChip type={request.requestsServiceInvestigation[0].serviceInvestigation.service.type} /> : "", 
                    scheduled : request.appointment ? true : false,
                    totalDays: calculateTotalDays(request, requestServiceType),
                    dateAdded: fullDateFromPostgresString(activeLanguageCode, request.createdAt),
                    dateAddedRaw: request.createdAt
                }
            });
            
            const rows = await Promise.all(rowPromises);
            setRows(rows);
            setFilteredRows(rows);
        };

        loadRows();
    }, [requests]);

    function applyFilterGeneral(filter:number[] , functionAdd: (value:number[]) => void, value:number){
        if(filter && filter.includes(value)){
            if(filter.length === 1){
                functionAdd([]);
            }
            else{
                functionAdd(filter.filter((s) => s !== value));
            }
        }
        else if(filter){
            functionAdd([...filter, value]);
        }
        else{
            functionAdd([value]);
        }
    }

    function applyStatusFilter(status:RequestStatus){
        applyFilterGeneral(statusFilter, setStatusFilter, status);
    }

    function iconType(id:number){
        const request = requests.find((req) => req.id === id);
        if(request && request.appointment){
            return "appointment_available";
        }
        return "appointment";
    }

    function colorOfAppointment(id:number){
        const request = requests.find((req) => req.id === id);
        if(request && request.appointment){
            return "green";
        }
        return "black";
    }

    function extraFilters(){
        if(requestServiceType === ServiceType.SURGERY){
            return (
                <Grid container spacing={2}>
                    <Grid item>
                        <FormControl variant="outlined" size="small" style={{ minWidth: '300px' }}>
                            <InputLabel htmlFor="select-procedure" id="select-procedure-label" style={{backgroundColor:'white'}}>
                                <Translate id="hospital.request.select_procedure" />
                            </InputLabel>
                            <Select
                                labelId="select-procedure-label"
                                id="select-procedure"
                                style={{ minWidth: '200px' }}
                                value={procedureFilter}
                                onChange={(e) => setProcedureFilter(e.target.value as string)}
                                displayEmpty
                                renderValue={(value) => value }
                                endAdornment={
                                    procedureFilter ? (
                                        <IconButton
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setProcedureFilter("");
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    ) : null
                                }
                            >
                                {proceduresApplied.map((procedure) => (
                                    <MenuItem key={procedure} value={procedure}>{procedure}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl variant="outlined" size="small" style={{ minWidth: '300px' }}>
                            <InputLabel htmlFor="select-surgeon" id="select-surgeon-label" style={{backgroundColor:'white'}}>
                                <Translate id="pages.hospital.services.request.select_surgeon" />
                            </InputLabel>
                            <Select
                                labelId="select-surgeon-label"
                                id="select-surgeon"
                                style={{ minWidth: '200px' }}
                                value={surgeonsFilter}
                                onChange={(e) => setSurgeonsFilter(e.target.value as string)}
                                displayEmpty
                                renderValue={(value) => {
                                    if (!value) return <Translate id="pages.hospital.services.request.select_surgeon" />;
                                    const staff = surgeonsScheduled.find(s => s.uuid === value);
                                    return staff ? researcherFullName(staff) : <Translate id="pages.hospital.services.request.select_surgeon" />;
                                }}
                                endAdornment={
                                    surgeonsFilter ? (
                                        <IconButton
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSurgeonsFilter("");
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    ) : null
                                }
                            >
                                {surgeonsScheduled.map((researcher) => (
                                    <MenuItem key={researcher.uuid} value={researcher.uuid}>{researcherFullName(researcher)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <div style={{ position: 'relative', minWidth: '200px' }}>
                            <DatePicker
                                label={<Translate id="hospital.request.date_completed_start" />}
                                value={startDateFilter}
                                onChange={(newValue) => setStartDateFilter(newValue)}
                                format={formatDateByLocale(activeLanguageCode)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                            {startDateFilter && (
                                <IconButton
                                    size="small"
                                    style={{ 
                                        position: 'absolute', 
                                        right: 8, 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setStartDateFilter(null);
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                    </Grid>
                    <Grid item>
                        <div style={{ position: 'relative', minWidth: '200px' }}>
                            <DatePicker
                                label={<Translate id="hospital.request.date_completed_end" />}
                                value={endDateFilter}
                                onChange={(newValue) => setEndDateFilter(newValue)}
                                format={formatDateByLocale(activeLanguageCode)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                            {endDateFilter && (
                                <IconButton
                                    size="small"
                                    style={{ 
                                        position: 'absolute', 
                                        right: 8, 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        zIndex: 1
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEndDateFilter(null);
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                    </Grid>
                </Grid>
            )
        }
        return null;
    }

    function handleBillingClick(id:number){
        const request = requests.find((req) => req.id === id);
        if(!request){
            console.log("No se encontró la solicitud");
        }
        goToBillingPatientCallback(request.requestsServiceInvestigation[0].patientInvestigation.uuid);
    }

    async function handlePDFClick(id:number){
        const request = requests.find((req) => req.id === id);
        if(!request){
            console.log("No se encontró la solicitud");
            return;
        }
        
        setShowModalPDF(true);
        setLoadingPDFData(true);
        
        const patient = await fetchPatient(request.requestsServiceInvestigation[0].patientInvestigation.uuid);
        setPatientForPDF(patient);

        // Fetch patient and full submission data for PDF
        if(request.submissionPatient){
            // Fetch the complete submission with all surveyRecords
            const submissionData = await getSubmissionPatientService(uuidInvestigation, request.submissionPatient.id, false);
            console.log("submissionData", JSON.stringify(submissionData, null, 2));
            setFullSubmission(submissionData.submission);
        }
        
        setRequestPDF(request);
        setLoadingPDFData(false);
    }

    function handleAppointmentClick(id:number){
        const request = requests.find((req) => req.id === id);
        if(!request){
            console.log("No se encontró la solicitud");
        }
        else if(request.appointment){
            console.log("Ya existe una cita para esta solicitud");
            goToAppointmentFromRequestCallback(request.appointment.uuid);
        }
        else{
            createAppointmentFromRequestCallback(id)
        }
    }
    function fillRequest(id:number){
        const request = requests.find((req) => req.id === id);
        if(request){
            //Lo comento porque no entiendo por que solo permito entrar para algunos casos
            // const hasActionableRequests = ACTIONABLE_REQUESTS.filter(function(status) {
            //     if(serviceType === RequestType.PHARMACY){
            //         return status === request.status
            //     }
            //     return request.requestsServiceInvestigation.findIndex((req) => req.status === status) !== -1
            // }).length > 0;
            const hasActionableRequests = true;
            if(hasActionableRequests){
                callBackRequestSelected(request);
            }
            else{
                console.log("No hay survey");
            }
        }
        else{
            console.log("No hay nada que hacer con esa solicitud");
        }
        
    }
    if(loading || !departments){
        return <Loader />
    }
    else if(requests.length === 0){
        return(
            <div className="text-center">
                <h4><Translate id="pages.hospital.services.no_requests" /></h4>
            </div>
        )
    }
    let headCells: HeadCell[] = [];
    if(requestServiceType === ServiceType.PHARMACY){
        headCells = [
            { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
            {id : 'researcher', label: <Translate id="hospital.staff" />, alignment:'left'},
            {id : 'status', label: <Translate id="pages.hospital.services.status" />, alignment:'left'},
            {id : 'department', label: <Translate id="hospital.departments.department" />, alignment:'left'},
            {id : 'date', label: <Translate id="general.date" />, alignment:'left'},
        ];
    }
    else if(requestServiceType === ServiceType.SURGERY){
        headCells = [
            {id : 'mainSurgeon', label: <Translate id="pages.hospital.services.main_surgeon" />, alignment:'left'},
            {id : 'department', label: <Translate id="hospital.departments.department" />, alignment:'left'},
            {id : 'service', label: <Translate id={`pages.hospital.services.${requestServiceType === ServiceType.SURGERY ? "surgery" : "service"}`} />, alignment:'left'},
            
            {id : 'dateAdded', label: <Translate id="general.date_added" />, alignment:'left'},
            {id : 'dateCompleted', label: <Translate id="hospital.request.dateCompleted" />, alignment:'left'},
            {id : 'totalDays', label: <Translate id="hospital.request.total_days" />, alignment:'left'},
            {id : 'status', label: <Translate id="pages.hospital.services.status" />, alignment:'left'}];
        
    }
    else{
        headCells = [
            {id : 'id', label: <Translate id="pages.hospital.services.request.request_id" />, alignment:'left'},
            {id : 'requester', label: <Translate id="hospital.staff" />, alignment:'left'},
            {id : 'department', label: <Translate id="hospital.departments.department" />, alignment:'left'},
            {id : 'service', label: <Translate id="hospital.request.services" />, alignment:'left'},
            {id : 'dateAdded', label: <Translate id="general.date" />, alignment:'left'},
            {id : 'status', label: <Translate id="pages.hospital.services.status" />, alignment:'left'}
        ]      
    }

    if(!uuidPatient){
        
        headCells.splice(0, 0, {id : 'nhc', label: <Translate id={stringHealthID} />, alignment:'left'})
        headCells.splice(1, 0, {id : 'patient', label: <Translate id="general.patient" />, alignment:'left'})
        headCells.splice(2, 0,{id : 'dob', label: <Translate id="investigation.create.personal_data.short-fields.birthdate" />, alignment:'left'})
    }
     
    let actions: any[] | null = null;
    if(requestServiceType === ServiceType.SURGERY){
        actions = [{type : (id:number) => iconType(id), color: (id:number) => colorOfAppointment(id), func : (id:number) => handleAppointmentClick(id), check: (row:Row) => row.statusValue !== RequestStatus.CANCELED}];
        
        // Only add billing action if billing functionality is enabled
        if(isBillingEnabled()) {
            actions.push({type : () => "billing" as const, color: () => "black", func : (id:number) => handleBillingClick(id), check: (row:Row) => row.statusValue !== RequestStatus.CANCELED});
        }
        
        // Add PDF action for surgery requests with submissions
        actions.push({type : () => "pdf" as const, color: () => "red", func : (id:number) => handlePDFClick(id), check: (row:Row) => row.submissionPatient != null});
    }
    else{
        actions = [{type : () => "pdf", color: () => "red", func : (id:number) => handlePDFClick(id)}]
    }
    
    return (
        <Grid container spacing={2}>
            <Modal open={showModalPDF} closeModal={() => {
                setShowModalPDF(false);
                setFullSubmission(null);
                setPatientForPDF(null);
                setRequestPDF(null);
                setLoadingPDFData(false);
            }}>
                {loadingPDFData ? (
                    <Loader />
                ) : requestPDF && pdfConfig && pdfConfig.config ? (
                    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                        <h3><Translate id="pages.request.modal.download_title" />{requestPDF.id}</h3>
                        <p><Translate id="pages.request.modal.download_description" /></p>
                        
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <PDFDownloadButton
                                document={
                                    <RequestPDFDocument 
                                        request={{
                                            ...requestPDF,
                                            id: requestPDF.id.toString(),
                                            createdAt: requestPDF.createdAt instanceof Date ? requestPDF.createdAt.toISOString() : requestPDF.createdAt,
                                            completionDate: requestPDF.completedAt ? (requestPDF.completedAt instanceof Date ? requestPDF.completedAt.toISOString() : requestPDF.completedAt) : undefined
                                        } as any}
                                        patient={patientForPDF || {
                                            personalData: {
                                                name: 'Unknown',
                                                surnames: 'Patient',
                                                birthdate: '',
                                                sex: '',
                                                phone: '',
                                                healthId: '',
                                                nationalId: '',
                                                passportNumber: '',
                                            }
                                        }}
                                        submission={fullSubmission}
                                        config={pdfConfig.config}
                                        locale={normalizedLocale}
                                        uuidInvestigation={currentInvestigationUuid || uuidInvestigation || ''}
                                    />
                                }
                                filename={`${t('pages.request.pdf.filename', 'medical-request')}-${requestPDF.id}`}
                                variant="contained"
                                size="large"
                                onSuccess={() => {
                                    console.log('PDF downloaded successfully');
                                }}
                                onError={(error) => {
                                    console.error('PDF download failed:', error);
                                }}
                                locale={normalizedLocale}
                            >
                                <Translate id="general.download_pdf" />
                            </PDFDownloadButton>
                        </div>
                        
                        <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
                            <p><strong><Translate id="pages.request.modal.pdf_content_title" /></strong></p>
                            <ul>
                                <li><Translate id="pages.request.modal.pdf_content_patient_info" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_request_details" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_services" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_clinical_notes" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_survey_records" /></li>
                                <li><Translate id="pages.request.modal.pdf_content_signature" /></li>
                            </ul>
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
                        <h3>Configuration Error</h3>
                        <p style={{ color: 'red' }}>
                            PDF configuration is not available. Please check billing information.
                        </p>
                    </div>
                )}
            </Modal>
            {
                !noSearchBox && 
                <SearchBox textField={{label:searchFor, callBack:setSearchText}}
                    activeFilters={statusFilter}
                    extraFilters={extraFilters}
                    filterItems={filterItems} />
            }
            <Grid item xs={12}>
                <EnhancedTable  selectRow={(id:number) => fillRequest(id)} noHeader noSelectable 
                    rows={filteredRows} headCells={headCells} actions={actions}
                     />  
            </Grid>
        </Grid>
    );
};
